import React, { useState } from 'react';
import { useTranslation } from 'localization';
import type { ApproachReference } from 'types/approach';
import type { FilterExpression, FilterOperator } from 'types/filters';
import TagAtom from '../../atoms/Tag/TagAtom';
import './SubApproachReferenceMolecule.scss';

export interface SubApproachReferenceMoleculeProps {
  reference: ApproachReference;
  displayName?: string;
  description?: string;
  /** Optional href for the linked detail page. When present the name renders as a link. */
  href?: string;
  /**
   * Renderer for the nested approach detail when the user expands the row.
   * Decoupled to avoid a molecule -> organism import.
   */
  renderNestedReader?: (ref: ApproachReference) => React.ReactNode;
  className?: string;
}

interface OverrideDescriptor {
  key: string;
  i18nKey: string;
  params: Record<string, unknown>;
}

type TFn = (key: string, params?: Record<string, unknown>) => string;

const OVERRIDE_KEY_PREFIX =
  'marketplace.approachDetail.whatsIncluded.subApproach.overrides';

function formatValue(value: unknown, t: TFn): string {
  if (typeof value === 'boolean') {
    return t(
      value
        ? 'marketplace.approachDetail.whatsIncluded.subApproach.boolean.on'
        : 'marketplace.approachDetail.whatsIncluded.subApproach.boolean.off',
    );
  }
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.replaceAll('_', ' ');
  return JSON.stringify(value);
}

function describeOverride(
  path: string,
  value: unknown,
  t: TFn,
): OverrideDescriptor {
  const parts = path.split('.');
  // orbit.schedules.<id>.<field>
  if (parts[0] === 'orbit' && parts[1] === 'schedules') {
    const which = parts[2] ?? '';
    const field = parts[3];
    if (field === 'spec' || field === 'english') {
      return {
        key: path,
        i18nKey: `${OVERRIDE_KEY_PREFIX}.orbitScheduleRetime`,
        params: { schedule: which, value: formatValue(value, t) },
      };
    }
    if (field === 'timezone') {
      return {
        key: path,
        i18nKey: `${OVERRIDE_KEY_PREFIX}.orbitScheduleTimezone`,
        params: { schedule: which, value: formatValue(value, t) },
      };
    }
    return {
      key: path,
      i18nKey: `${OVERRIDE_KEY_PREFIX}.orbitScheduleField`,
      params: { schedule: which, field: field ?? '', value: formatValue(value, t) },
    };
  }
  // factors.<id>.<field>
  if (parts[0] === 'factors' && parts[1]) {
    const fid = parts[1].replace(/^f[a-z]_/, '');
    const field = parts[2];
    if (field === 'weight' && typeof value === 'number') {
      return {
        key: path,
        i18nKey: `${OVERRIDE_KEY_PREFIX}.factorWeight`,
        params: { factor: fid, percent: Math.round(value * 100) },
      };
    }
    if (field === 'enabled') {
      return {
        key: path,
        i18nKey: value
          ? `${OVERRIDE_KEY_PREFIX}.factorEnabled`
          : `${OVERRIDE_KEY_PREFIX}.factorDisabled`,
        params: { factor: fid },
      };
    }
    return {
      key: path,
      i18nKey: `${OVERRIDE_KEY_PREFIX}.factorField`,
      params: { factor: fid, field: field ?? '', value: formatValue(value, t) },
    };
  }
  // arc.<field> (also covers arcs.<field>)
  if (parts[0] === 'arc' || parts[0] === 'arcs') {
    const field = parts[parts.length - 1];
    if (field === 'defaultDeadlineDays') {
      return {
        key: path,
        i18nKey: `${OVERRIDE_KEY_PREFIX}.arcDeadline`,
        params: { days: value },
      };
    }
    if (field === 'autoComplete') {
      return {
        key: path,
        i18nKey: value
          ? `${OVERRIDE_KEY_PREFIX}.arcAutoCompleteOn`
          : `${OVERRIDE_KEY_PREFIX}.arcAutoCompleteOff`,
        params: {},
      };
    }
    return {
      key: path,
      i18nKey: `${OVERRIDE_KEY_PREFIX}.arcField`,
      params: { field: field ?? '', value: formatValue(value, t) },
    };
  }
  // deploymentSettings.<field>
  if (parts[0] === 'deploymentSettings') {
    const field = parts[1];
    if (field === 'conflictStrategy') {
      return {
        key: path,
        i18nKey: `${OVERRIDE_KEY_PREFIX}.deployConflictStrategy`,
        params: { value: formatValue(value, t) },
      };
    }
    if (field === 'allowRollback') {
      return {
        key: path,
        i18nKey: value
          ? `${OVERRIDE_KEY_PREFIX}.deployAllowsRollback`
          : `${OVERRIDE_KEY_PREFIX}.deployDisallowsRollback`,
        params: {},
      };
    }
    if (field === 'maxCascadeDepth') {
      return {
        key: path,
        i18nKey: `${OVERRIDE_KEY_PREFIX}.deployMaxCascadeDepth`,
        params: { value },
      };
    }
    return {
      key: path,
      i18nKey: `${OVERRIDE_KEY_PREFIX}.deployField`,
      params: { field: field ?? '', value: formatValue(value, t) },
    };
  }
  // Fallback — humanize the path
  const friendly = path.replaceAll('.', ' › ').replaceAll('_', ' ');
  return {
    key: path,
    i18nKey: `${OVERRIDE_KEY_PREFIX}.fallback`,
    params: { path: friendly, value: formatValue(value, t) },
  };
}

function flattenOverrides(
  overrides: Record<string, unknown>,
  prefix = '',
): Array<[string, unknown]> {
  const out: Array<[string, unknown]> = [];
  for (const [key, value] of Object.entries(overrides)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      out.push(...flattenOverrides(value as Record<string, unknown>, path));
    } else {
      out.push([path, value]);
    }
  }
  return out;
}

const OPERATOR_KEY: Record<FilterOperator, string> = {
  equals: 'equals',
  not_equals: 'notEquals',
  greater_than: 'greaterThan',
  less_than: 'lessThan',
  greater_than_equals: 'greaterThanEquals',
  less_than_equals: 'lessThanEquals',
  contains: 'contains',
  not_contains: 'notContains',
  in: 'in',
  not_in: 'notIn',
  exists: 'exists',
  not_exists: 'notExists',
  starts_with: 'startsWith',
  ends_with: 'endsWith',
  is_true: 'isTrue',
  is_false: 'isFalse',
  is_empty: 'isEmpty',
  is_not_empty: 'isNotEmpty',
  between: 'between',
  date_within_days: 'dateWithinDays',
  last_7_days: 'last7Days',
  last_30_days: 'last30Days',
  within_last_days: 'withinLastDays',
  within_next_days: 'withinNextDays',
  contains_all: 'containsAll',
};

function formatCondition(condition: FilterExpression, t: TFn): string {
  const opKey = OPERATOR_KEY[condition.operator] ?? 'equals';
  const opLabel = t(
    `marketplace.approachDetail.whatsIncluded.subApproach.condition.operator.${opKey}`,
  );
  const value =
    condition.values !== undefined
      ? condition.values.map((v) => formatValue(v, t)).join(', ')
      : condition.value !== undefined
        ? formatValue(condition.value, t)
        : '';
  return t(
    'marketplace.approachDetail.whatsIncluded.subApproach.condition.template',
    {
      property: condition.property,
      operator: opLabel,
      value,
    },
  );
}

const SubApproachReferenceMolecule: React.FC<
  SubApproachReferenceMoleculeProps
> = ({
  reference,
  displayName,
  description,
  href,
  renderNestedReader,
  className = '',
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const isInclude = reference.mode === 'include';
  const name = displayName || reference.approachSystemName;

  const requires = reference.requires ?? [];
  const overrideEntries =
    reference.overrides && typeof reference.overrides === 'object'
      ? flattenOverrides(reference.overrides as Record<string, unknown>)
      : [];

  const canExpand = !!renderNestedReader;

  const rootClasses = [
    'marketplace-sub-approach-ref',
    `marketplace-sub-approach-ref--${reference.mode}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const deployToLabel = reference.deployTo
    ? reference.deployTo === 'ancestor'
      ? t(
          'marketplace.approachDetail.whatsIncluded.subApproach.deployTo.ancestor',
        )
      : t(
          'marketplace.approachDetail.whatsIncluded.subApproach.deployTo.target',
        )
    : null;

  return (
    <article
      className={rootClasses}
      data-mode={reference.mode}
      data-system-name={reference.approachSystemName}
    >
      <header className="marketplace-sub-approach-ref__header">
        <i
          className={`pi ${
            isInclude ? 'pi-clone' : 'pi-link'
          } marketplace-sub-approach-ref__mode-icon`}
          aria-hidden="true"
        />
        {href ? (
          <a className="marketplace-sub-approach-ref__name" href={href}>
            {name}
          </a>
        ) : (
          <span className="marketplace-sub-approach-ref__name">{name}</span>
        )}
        {/*
         * Mode pill (Include / Dependency / Prerequisite) intentionally removed:
         * the bucket title already conveys the relationship to the user.
         * Internal mode is preserved on the article via data-mode for styling.
         */}
        {deployToLabel && (
          <TagAtom
            tone={reference.deployTo === 'ancestor' ? 'danger' : 'outline'}
            label={deployToLabel}
          />
        )}
        {reference.order != null && (
          <TagAtom
            tone="neutral"
            label={t(
              'marketplace.approachDetail.whatsIncluded.subApproach.mergeOrder',
              { order: reference.order },
            )}
          />
        )}
        {reference.approachVersion != null && (
          <span className="marketplace-sub-approach-ref__version">
            {t(
              'marketplace.approachDetail.whatsIncluded.subApproach.versionLabel',
              { version: reference.approachVersion },
            )}
          </span>
        )}
        {/* V2: deployment-status badge slot — needs buyer-sphere context */}
        {canExpand && (
          <button
            type="button"
            className="marketplace-sub-approach-ref__toggle"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            <i
              className={`pi ${
                expanded ? 'pi-chevron-up' : 'pi-chevron-down'
              } marketplace-sub-approach-ref__toggle-chevron`}
              aria-hidden="true"
            />
            <span className="marketplace-sub-approach-ref__toggle-label">
              {expanded
                ? t(
                    'marketplace.approachDetail.whatsIncluded.subApproach.hideNested',
                  )
                : t(
                    'marketplace.approachDetail.whatsIncluded.subApproach.showNested',
                  )}
            </span>
          </button>
        )}
      </header>

      {description && (
        <p className="marketplace-sub-approach-ref__description">{description}</p>
      )}

      {!isInclude && requires.length > 0 && (
        <section
          className="marketplace-sub-approach-ref__requires"
          aria-labelledby={`${reference.approachSystemName}-requires-heading`}
        >
          <h4
            id={`${reference.approachSystemName}-requires-heading`}
            className="marketplace-sub-approach-ref__section-heading"
          >
            {t(
              'marketplace.approachDetail.whatsIncluded.subApproach.requiresHeading',
            )}
          </h4>
          <ul className="marketplace-sub-approach-ref__requires-list">
            {requires.map((req) => (
              <li
                key={req.outputKey}
                className="marketplace-sub-approach-ref__requires-row"
              >
                <code className="marketplace-sub-approach-ref__requires-key">
                  {req.outputKey}
                </code>
                <span
                  className="marketplace-sub-approach-ref__requires-arrow"
                  aria-hidden="true"
                >
                  ←
                </span>
                <code className="marketplace-sub-approach-ref__requires-value">
                  {req.fromResource}
                </code>
              </li>
            ))}
          </ul>
        </section>
      )}

      {isInclude && overrideEntries.length > 0 && (
        <section
          className="marketplace-sub-approach-ref__overrides"
          role="note"
          aria-labelledby={`${reference.approachSystemName}-overrides-heading`}
        >
          <h4
            id={`${reference.approachSystemName}-overrides-heading`}
            className="marketplace-sub-approach-ref__overrides-heading"
          >
            <i
              className="pi pi-pencil marketplace-sub-approach-ref__overrides-icon"
              aria-hidden="true"
            />
            {t(
              'marketplace.approachDetail.whatsIncluded.subApproach.overridesHeading',
            )}
          </h4>
          <ul className="marketplace-sub-approach-ref__overrides-list">
            {overrideEntries.map(([path, value]) => {
              const desc = describeOverride(path, value, t);
              return (
                <li
                  key={desc.key}
                  className="marketplace-sub-approach-ref__overrides-row"
                >
                  <i
                    className="pi pi-arrow-right marketplace-sub-approach-ref__overrides-arrow"
                    aria-hidden="true"
                  />
                  <span className="marketplace-sub-approach-ref__overrides-text">
                    {t(desc.i18nKey, desc.params)}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {reference.condition && (
        <p className="marketplace-sub-approach-ref__condition">
          <i
            className="pi pi-filter marketplace-sub-approach-ref__condition-icon"
            aria-hidden="true"
          />
          <span>{formatCondition(reference.condition, t)}</span>
        </p>
      )}

      {canExpand && expanded && renderNestedReader && (
        <div className="marketplace-sub-approach-ref__nested">
          {renderNestedReader(reference)}
        </div>
      )}
    </article>
  );
};

SubApproachReferenceMolecule.displayName = 'SubApproachReferenceMolecule';

export default SubApproachReferenceMolecule;
